/* eslint-disable react/prop-types */
import ReactFlow, { ReactFlowProvider, useReactFlow } from "reactflow";
import {
  Typography,
  IconButton,
  Box,
  Drawer,
  TextField,
  Button,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { HelpOutline } from "@mui/icons-material";
import NewWorkflowUserGuide from "@/components/dialogs/NewWorkflowUserGuide";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTheme, lighten, darken } from "@mui/material/styles";
import { getSingleSelectStyles } from "@/lib/styles";
import useWorkflowData from "./useNewWorkflowData";
import "reactflow/dist/style.css";
import Toast from "@/components/toast";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PtwSettingsData } from "./workFlow-context";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import StepModalDialog from "./components/StepModalDialog";
import DeleteStepInputDialog from "./components/DeleteStepInputDialog";

const animatedComponents = makeAnimated();

// Child component to handle ReactFlow and fitView logic
const FlowContainer = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  drawerOpen,
  isDarkMode,
  t,
}) => {
  const { fitView } = useReactFlow();
  const theme = useTheme();

  useEffect(() => {
    if (nodes.length > 0) {
      const timeout = setTimeout(() => {
        fitView({ padding: 0.1, duration: 200 });
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [nodes, edges, drawerOpen, fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      defaultEdgeOptions={{ animated: true }}
      fitView
      fitViewOptions={{ padding: 0.1 }}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 8,
        border: `1px solid ${
          isDarkMode
            ? lighten(theme.palette.divider, 0.8)
            : darken(theme.palette.divider, 0.5)
        }`,
      }}
      aria-label={t("newWorkFlow.flowAria")}
    />
  );
};

const NewWorkFlow = () => {
  const { t } = useTranslation();
  const { goBackToList } = PtwSettingsData();

  // State declarations first
  const [deleteStepConfirmation, setDeleteStepConfirmation] = useState({
    open: false,
    nodeId: null,
    stepName: "",
  });
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  // Define handlers before hook usage
  const handleDeleteStep = (nodeId) => {
    setDeleteStepConfirmation({
      open: true,
      nodeId: nodeId,
      stepName: `Step ${nodeId.slice(0, 8)}`, // Will be updated with proper name
    });
  };

  const {
    toast,
    handleToastClose,
    postIsLoading,
    nodeData,
    setNodeData,
    nodes,
    onNodesChange,
    edges,
    onEdgesChange,
    isModalOpen,
    userGroupIds,
    modalButton,
    workflowData,
    changeTarget,
    closeModal,
    updateNodeData,
    addChildNode,
    deleteNode,
    onConnect,
    changeHandlerName,
    handleSubmit,
    handleMultiChange,
    inputTypeList,
    userGroupsList,
  } = useWorkflowData(handleDeleteStep);

  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [optionInputs, setOptionInputs] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    stepIndex: null,
    stepName: "",
  });
  const isDarkMode = theme.palette.mode === "dark";

  const drawerWidth = drawerOpen ? 360 : 60;
  // Dynamic toolbar height with extra spacing for app bar shadows/styling
  const toolbarHeight = (theme.mixins.toolbar.minHeight || 64) + 16;
  const isValid = workflowData?.name?.length > 2;

  const targetOptions = [
    { value: "WORK_PERMIT", label: t("newWorkFlow.targetWorkPermit") },
    { value: "OBSERVATION", label: t("newWorkFlow.targetObservation") },
  ];

  // Handler functions for StepModalDialog
  const handleDeleteStepInput = (index) => {
    setDeleteConfirmation({
      open: true,
      stepIndex: index,
      stepName: nodeData.step_inputs?.[index]?.name || `Input ${index + 1}`,
    });
  };

  const cancelDeleteStepInput = () => {
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  const confirmDeleteStepInput = () => {
    if (deleteConfirmation.stepIndex !== null) {
      const index = deleteConfirmation.stepIndex;
      setNodeData((prevNodeData) => {
        const newInputType = [
          ...(Array.isArray(prevNodeData.step_inputs)
            ? prevNodeData.step_inputs
            : []),
        ];
        newInputType.splice(index, 1);
        return { ...prevNodeData, step_inputs: newInputType };
      });
      setOptionInputs((prev) => {
        const newInputs = { ...prev };
        delete newInputs[index];
        return newInputs;
      });
    }
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  const handleOptionInputChange = (index, value) => {
    setOptionInputs((prev) => ({ ...prev, [index]: value }));
  };

  const handleAddOption = (index) => {
    const optionInput = optionInputs[index] || "";
    if (!optionInput.trim()) return;

    setNodeData((prevNodeData) => {
      const newInputType = [...prevNodeData.step_inputs];
      newInputType[index] = {
        ...newInputType[index],
        options: [...(newInputType[index].options || []), optionInput.trim()],
      };
      return {
        ...prevNodeData,
        step_inputs: newInputType,
      };
    });

    setOptionInputs((prev) => ({ ...prev, [index]: "" }));
  };

  // Validate all required fields, including step_inputs and options
  useEffect(() => {
    const validateForm = () => {
      const staticFieldsValid =
        userGroupIds.length > 0 &&
        Boolean(nodeData.action_name) &&
        Boolean(nodeData.status);

      if (!staticFieldsValid) return false;

      if (
        !Array.isArray(nodeData.step_inputs) ||
        nodeData.step_inputs.length === 0
      ) {
        return true;
      }

      const stepInputsValid = nodeData.step_inputs.every((step_input) => {
        if (!step_input.input_type_id || !step_input.name) {
          return false;
        }

        const inputType = inputTypeList.find(
          (option) => option.value === step_input.input_type_id
        );
        if (
          inputType &&
          ["selection", "multi_selection"].includes(inputType.input_key) &&
          (!step_input.options || step_input.options.length === 0)
        ) {
          return false;
        }

        return true;
      });

      return staticFieldsValid && stepInputsValid;
    };

    setIsFormValid(validateForm());
  }, [
    userGroupIds.length,
    nodeData.action_name,
    nodeData.status,
    nodeData.step_inputs,
    inputTypeList,
  ]);

  const handleInputTypeChange = (index, field, value) => {
    setNodeData((prevNodeData) => {
      const newInputType = [
        ...(Array.isArray(prevNodeData.step_inputs)
          ? prevNodeData.step_inputs
          : []),
      ];

      let newValue = value;

      if (field === "input_type_id") {
        newValue = typeof value === "object" ? value.value : value;
        const selectedInputType = inputTypeList.find(
          (option) => option.value === newValue
        );
        newInputType[index] = {
          ...newInputType[index],
          input_type_id: newValue,
          type: selectedInputType ? selectedInputType.input_key : "",
          options: [],
        };
      } else if (field === "position") {
        newValue = parseInt(value, 10);
        if (isNaN(newValue) || newValue < 0) newValue = 0;
      } else {
        newInputType[index] = {
          ...newInputType[index],
          [field]: newValue,
        };
      }

      if (!newInputType[index]) {
        newInputType[index] = {
          input_type_id: "",
          type: "",
          name: "",
          description: "",
          required: false,
          default_value: "",
          position: 0,
          options: [],
        };
      }

      return {
        ...prevNodeData,
        step_inputs: newInputType,
      };
    });
  };

  const removeOption = (stepIndex, optionIndex) => {
    setNodeData((prevNodeData) => {
      const newInputType = [...prevNodeData.step_inputs];
      newInputType[stepIndex] = {
        ...newInputType[stepIndex],
        options: newInputType[stepIndex].options.filter(
          (_, i) => i !== optionIndex
        ),
      };
      return {
        ...prevNodeData,
        step_inputs: newInputType,
      };
    });
  };

  const moveStepInputUp = (index) => {
    if (index === 0) return;
    setNodeData((prevNodeData) => {
      const steps = [...prevNodeData.step_inputs];
      [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];

      const updatedSteps = steps.map((step, idx) => ({
        ...step,
        position: idx,
      }));

      return { ...prevNodeData, step_inputs: updatedSteps };
    });
  };

  const moveStepInputDown = (index) => {
    if (index === nodeData.step_inputs.length - 1) return;
    setNodeData((prevNodeData) => {
      const steps = [...prevNodeData.step_inputs];
      [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];

      const updatedSteps = steps.map((step, idx) => ({
        ...step,
        position: idx,
      }));

      return { ...prevNodeData, step_inputs: updatedSteps };
    });
  };

  const addNewStepInput = () => {
    setNodeData((prevNodeData) => {
      const existingSteps = Array.isArray(prevNodeData.step_inputs)
        ? prevNodeData.step_inputs
        : [];

      const nextPosition =
        existingSteps.length > 0
          ? Math.max(...existingSteps.map((step) => step.position)) + 1
          : 0;

      return {
        ...prevNodeData,
        step_inputs: [
          ...existingSteps,
          {
            input_type_id: "",
            type: "",
            name: "",
            description: "",
            required: false,
            default_value: "",
            position: nextPosition,
            options: [],
          },
        ],
      };
    });
  };

  // Update step name when dialog opens and workflowData is available
  useEffect(() => {
    if (
      deleteStepConfirmation.open &&
      deleteStepConfirmation.nodeId &&
      workflowData?.steps
    ) {
      const step = workflowData.steps.find(
        (s) => s.id === deleteStepConfirmation.nodeId
      );
      const stepName =
        step?.status ||
        step?.action_name ||
        `${t("newWorkFlow.step")} ${deleteStepConfirmation.nodeId.slice(0, 8)}`;

      if (stepName !== deleteStepConfirmation.stepName) {
        setDeleteStepConfirmation((prev) => ({
          ...prev,
          stepName: stepName,
        }));
      }
    }
  }, [
    deleteStepConfirmation.open,
    deleteStepConfirmation.nodeId,
    workflowData?.steps,
    t,
    deleteStepConfirmation.stepName,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: `calc(100vh - ${toolbarHeight}px - 32px)`,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          mt: `${toolbarHeight}px`,
          p: 0,
          m: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            position: "relative",
            p: 0,
            m: 0,
          }}
        >
          {/* Back Button - Positioned consistently with other workflow components */}
          <Box
            sx={{
              position: "absolute",
              top: 32,
              left: 16,
              zIndex: 1000,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton
              sx={{
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={goBackToList}
              aria-label={t("newWorkFlow.backButtonAria")}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              sx={{
                bgcolor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.info.dark,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={() => setHelpModalOpen(true)}
              aria-label={t("newWorkFlow.helpButtonAria")}
            >
              <HelpOutline />
            </IconButton>
          </Box>
          <ReactFlowProvider>
            <FlowContainer
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              drawerOpen={drawerOpen}
              isDarkMode={isDarkMode}
              t={t}
            />
          </ReactFlowProvider>
        </Box>

        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            zIndex: theme.zIndex.drawer, // Standard drawer z-index
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              transition: "width 0.3s ease",
              bgcolor: theme.palette.background.paper,
              borderLeft: `1px solid ${
                isDarkMode
                  ? lighten(theme.palette.divider, 0.2)
                  : darken(theme.palette.divider, 0.2)
              }`,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              top: `${toolbarHeight}px`,
              height: `calc(100vh - ${toolbarHeight}px)`,
            },
          }}
          aria-label={t("newWorkFlow.drawerAria")}
        >
          <Box
            sx={{
              width: "100%",
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: drawerOpen ? "flex-end" : "center",
              px: 1,
            }}
          >
            <IconButton
              onClick={() => {
                setDrawerOpen(!drawerOpen);
              }}
              sx={{
                color: theme.palette.text.primary,
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
              aria-label={t(
                drawerOpen
                  ? "newWorkFlow.closeDrawerAria"
                  : "newWorkFlow.openDrawerAria"
              )}
            >
              {drawerOpen ? (
                <ChevronRightIcon fontSize="medium" />
              ) : (
                <ChevronLeftIcon fontSize="medium" />
              )}
            </IconButton>
          </Box>

          {drawerOpen && (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                p: 2,
                overflowY: "auto",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              aria-label={t("newWorkFlow.formAria")}
            >
              {/* Header Section */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    mb: 1,
                  }}
                >
                  {t("newWorkFlow.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("newWorkFlow.drawerDescription")}
                </Typography>
              </Box>

              {/* Workflow Configuration Section */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {t("newWorkFlow.workflowConfigTitle")}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "medium", mb: 1 }}
                  >
                    {t("newWorkFlow.workflowNameLabel")} *
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("newWorkFlow.workflowNameDescription")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={workflowData.name || ""}
                    onChange={changeHandlerName}
                    placeholder={t("newWorkFlow.workflowNamePlaceholder")}
                    error={!workflowData.name}
                    helperText={
                      !workflowData.name
                        ? t("newWorkFlow.workflowNameRequired")
                        : ""
                    }
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        minHeight: 48,
                        background: theme.palette.background.paper,
                      },
                    }}
                    aria-label={t("newWorkFlow.workflowNameAria")}
                  />

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "medium", mb: 1 }}
                  >
                    {t("newWorkFlow.targetLabel")} *
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("newWorkFlow.targetDescription")}
                  </Typography>
                  <Select
                    components={animatedComponents}
                    options={targetOptions}
                    value={
                      targetOptions.find(
                        (option) => option.value === workflowData.target
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      changeTarget(selectedOption?.value || "")
                    }
                    placeholder={t("newWorkFlow.targetPlaceholder")}
                    styles={{
                      ...singleSelectStyles,
                      control: (base) => ({
                        ...base,
                        minHeight: 48,
                        borderRadius: 4,
                        background: theme.palette.background.paper,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    aria-label={t("newWorkFlow.targetAria")}
                  />
                </Box>

                {/* Status Indicator */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("newWorkFlow.statusTitle")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: isValid ? "success.main" : "warning.main",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {isValid
                        ? t("newWorkFlow.readyToSave")
                        : t("newWorkFlow.fillRequired")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  mt: "auto",
                  pt: 3,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || postIsLoading}
                    size="large"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: "bold",
                      textTransform: "none",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                    }}
                    aria-label={t("newWorkFlow.saveWorkflowButtonAria")}
                  >
                    {postIsLoading && <span className="loader" />}{" "}
                    {t("newWorkFlow.saveWorkflowButton")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => window.history.back()}
                    size="large"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: "medium",
                      textTransform: "none",
                    }}
                    aria-label={t("newWorkFlow.cancelButtonAria")}
                  >
                    {t("newWorkFlow.cancelButton")}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>

      <StepModalDialog
        open={isModalOpen}
        onClose={closeModal}
        modalButton={modalButton}
        nodeData={nodeData}
        setNodeData={setNodeData}
        userGroupIds={userGroupIds}
        userGroupsList={userGroupsList}
        inputTypeList={inputTypeList}
        handleMultiChange={handleMultiChange}
        isFormValid={isFormValid}
        onSave={modalButton === "add" ? addChildNode : updateNodeData}
        onAddStep={addChildNode}
        handleInputTypeChange={handleInputTypeChange}
        addNewStepInput={addNewStepInput}
        handleDeleteStepInput={handleDeleteStepInput}
        moveStepInputUp={moveStepInputUp}
        moveStepInputDown={moveStepInputDown}
        optionInputs={optionInputs}
        handleOptionInputChange={handleOptionInputChange}
        handleAddOption={handleAddOption}
        removeOption={removeOption}
        translationPrefix="newWorkFlow"
      />

      <DeleteStepInputDialog
        open={deleteConfirmation.open}
        onClose={cancelDeleteStepInput}
        onConfirm={confirmDeleteStepInput}
        stepName={deleteConfirmation.stepName}
        translationPrefix="newWorkFlow"
      />

      {/* Delete Step Confirmation Dialog */}
      <DeleteStepInputDialog
        open={deleteStepConfirmation.open}
        onClose={() =>
          setDeleteStepConfirmation({ open: false, nodeId: null, stepName: "" })
        }
        onConfirm={() => {
          deleteNode(deleteStepConfirmation.nodeId);
          setDeleteStepConfirmation({
            open: false,
            nodeId: null,
            stepName: "",
          });
        }}
        stepName={deleteStepConfirmation.stepName}
        translationPrefix="newWorkFlow"
      />

      {/* User Guide Help Modal */}
      <NewWorkflowUserGuide
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
};

export default NewWorkFlow;
